import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useAuth } from './AuthContext';

const OnboardingContext = createContext(null);

export function OnboardingProvider({ children }) {
  const { authFetch, user } = useAuth();
  const [status, setStatus] = useState(null); // null = loading
  const [step, setStep] = useState(0);
  const [aiInstructions, setAiInstructions] = useState('');
  const [entryVisible, setEntryVisible] = useState(false);
  const [overlayVisible, setOverlayVisible] = useState(false);

  const save = useCallback(async (updates) => {
    try {
      await authFetch('/api/onboarding', {
        method: 'POST',
        body: JSON.stringify(updates),
      });
    } catch (e) {
      console.error('Onboarding save error:', e.message);
    }
  }, [authFetch]);

  useEffect(() => {
    if (!user) return;
    authFetch('/api/onboarding')
      .then(r => r.json())
      .then(data => {
        setStatus(data.status);
        setStep(data.step || 0);
        setAiInstructions(data.ai_instructions || '');
        if (data.status === 'not_started') {
          setEntryVisible(true);
        } else if (data.status === 'in_progress') {
          setOverlayVisible(true);
        }
      })
      .catch(() => setStatus('not_started'));
  }, [user]); // eslint-disable-line react-hooks/exhaustive-deps

  const start = async () => {
    setEntryVisible(false);
    setOverlayVisible(true);
    setStatus('in_progress');
    setStep(0);
    await save({ status: 'in_progress', step: 0 });
  };

  const skip = async () => {
    setEntryVisible(false);
    setOverlayVisible(false);
    setStatus('skipped');
    await save({ status: 'skipped' });
  };

  const goToStep = async (n) => {
    setStep(n);
    await save({ step: n });
  };

  const complete = async () => {
    setOverlayVisible(false);
    setStatus('completed');
    await save({ status: 'completed' });
  };

  const openHelp = () => {
    const startStep = status === 'in_progress' ? step : 0;
    setStep(startStep);
    setEntryVisible(false);
    setOverlayVisible(true);
    if (status !== 'in_progress') {
      setStatus('in_progress');
      save({ status: 'in_progress', step: startStep });
    }
  };

  const saveAiInstructions = async (val) => {
    setAiInstructions(val);
    await save({ ai_instructions: val });
  };

  return (
    <OnboardingContext.Provider value={{
      status, step, aiInstructions,
      entryVisible, overlayVisible,
      start, skip, goToStep, complete, openHelp, saveAiInstructions,
    }}>
      {children}
    </OnboardingContext.Provider>
  );
}

export function useOnboarding() {
  const ctx = useContext(OnboardingContext);
  if (!ctx) throw new Error('useOnboarding must be used within OnboardingProvider');
  return ctx;
}
